import { AlertTriangle, Heart, LogOut, Menu, MessageSquare, Search, ShieldCheck, ShoppingCart, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLogoutMutation } from "@/features/auth/hooks/useAuthMutations";
import { FloatingAccountDock } from "@/features/auth/components/AccountManagementPanel";
import { SmartSearchCommandPalette } from "@/features/smart-search/components/SmartSearchCommandPalette";
import { useMsnUnreadCount } from "@/features/messages/hooks/useMessages";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/lib/auth/authStore";
import { getMediaUrl } from "@/shared/utils/mediaUtils";
import { i18n, type AppLanguage } from "@/i18n";
import { useThemeStore, type AppTheme } from "@/lib/theme/themeStore";
import { Button, GlobalBackButton, Input } from "@/shared/ui";
import { HybridModeBanner }   from "@/shared/ui/HybridModeBanner";
import { PromoRibbonStrip }  from "@/shared/ui/PromoRibbonStrip";
import { NotificationPopover, useNotifications } from "@/features/notifications/NotificationPopover";
import { formatNavBadgeCount, useNavBadges } from "@/features/dashboard/hooks/useNavBadges";
import { SidebarNav }         from "@/app/layouts/SidebarNav";
import { Footer }             from "@/app/layouts/Footer";
import { FloatingVideoCall }  from "@/features/appointments/components/FloatingVideoCall";
import { FloatingShareControl } from "@/shared/ui/FloatingShareControl";
import { getBusinessWorkspaceQuickActions, getPreviewQuickActions, WorkspaceShortcutCarousel, type BusinessWorkspaceRole, type PreviewRole } from "@/features/dashboard-preview/workspaceShortcuts";
import { useMyAppointments } from "@/features/appointments/hooks/useAppointments";
import { useDoctorWorkspace } from "@/features/professional/api";
import { useWalletBalance } from "@/features/wallet/hooks/useWallet";

const themeOptions: Array<{ value: AppTheme; labelKey: string }> = [
  { value: "light-flat", labelKey: "themes.lightFlat" },
  { value: "dark", labelKey: "themes.dark" },
  { value: "gradient-light", labelKey: "themes.gradientLight" },
];

const languageOptions: AppLanguage[] = ["sq", "en", "sr", "tr"];
const marketplaceBadgeAccountTypes = ["basic", "patient"] as const;
const publicContentRoutePrefixes = ["/showcase", "/search", "/businesses", "/doctors"] as const;

const isPublicContentRoute = (pathname: string): boolean => {
  if (pathname === "/") {
    return true;
  }
  return publicContentRoutePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const isPersonalWorkspaceRoute = (pathname: string, profileRole: string | null): boolean => {
  if (pathname.startsWith("/app/dashboard-preview")) return false;
  if (profileRole === "patient") {
    return ["/app/patient", "/app/insurance", "/app/family"].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  }
  if (profileRole === "technician") {
    return ["/app/professional", "/app/laboratory"].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  }
  if (profileRole === "doctor" || profileRole === "specialist" || profileRole === "student") {
    return ["/app/professional", "/app/reports", "/app/doctor"].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  }
  return false;
};

const isBusinessWorkspaceRoute = (pathname: string): boolean => [
  "/app/business/manage",
  "/app/business/billing",
  "/app/business/commerce",
  "/app/service-hub",
  "/app/inventory",
  "/app/finance",
  "/app/analytics",
  "/app/hospital",
].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const compactCarouselValue = (value: number): string => (value > 99 ? "99+" : String(value));

/**
 * Shell kryesor per rruget e autentikuara `/app/*`.
 */
interface AppShellProps {
  children?: ReactNode;
}

export const AppShell = ({ children }: AppShellProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const {
    isSidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
    user,
    capabilities,
    activeFunctionalAccount,
    systemRole,
    profileRole,
    businessRole,
  } = useAuthStore();
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const logoutMutation = useLogoutMutation();
  const activeFunctionalAccountType = activeFunctionalAccount?.functional_account_type ?? null;
  const previewRole = ["patient", "doctor", "specialist", "technician", "student"].includes(profileRole ?? "")
    ? profileRole as PreviewRole
    : null;
  const showWorkspaceShortcuts =
    systemRole !== "superadmin" &&
    systemRole !== "admin" &&
    previewRole !== null &&
    isPersonalWorkspaceRoute(pathname, previewRole);
  const businessWorkspaceRole = ["owner", "staff", "technical", "doctor"].includes(businessRole ?? "")
    ? businessRole as BusinessWorkspaceRole
    : null;
  const showBusinessWorkspaceShortcuts =
    systemRole !== "superadmin" &&
    systemRole !== "admin" &&
    businessWorkspaceRole !== null &&
    isBusinessWorkspaceRoute(pathname);
  const isDoctorWorkspace = previewRole === "doctor" || previewRole === "specialist";
  const appointmentsQuery = useMyAppointments(showWorkspaceShortcuts && isDoctorWorkspace);
  const doctorWorkspaceQuery = useDoctorWorkspace({ source: "all" }, showWorkspaceShortcuts && isDoctorWorkspace);
  const walletQuery = useWalletBalance({ enabled: showWorkspaceShortcuts && isDoctorWorkspace });
  const workspaceBadgeQuery = useNavBadges(showWorkspaceShortcuts && isDoctorWorkspace);
  const doctorShortcutActions = isDoctorWorkspace
    ? getPreviewQuickActions(previewRole).map((action) => {
      if (action.label === "Agjenda") {
        const upcoming = (appointmentsQuery.data ?? []).filter((appointment) => new Date(appointment.start_at).getTime() >= Date.now()).length;
        return { ...action, badge: appointmentsQuery.isLoading ? "..." : compactCarouselValue(upcoming) };
      }
      if (action.label === "Pacientet") {
        return { ...action, badge: doctorWorkspaceQuery.isLoading ? "..." : compactCarouselValue(doctorWorkspaceQuery.data?.summary.patients ?? 0) };
      }
      if (action.label === "Workbench") {
        return { ...action, badge: doctorWorkspaceQuery.isLoading ? "..." : compactCarouselValue(doctorWorkspaceQuery.data?.summary.drafts ?? 0) };
      }
      if (action.label === "Wallet") {
        const wallet = walletQuery.data?.wallet;
        return { ...action, badge: wallet ? `${wallet.balance} ${wallet.currency}` : null };
      }
      if (action.label === "Blogu") {
        return { ...action, badge: workspaceBadgeQuery.isLoading ? "..." : compactCarouselValue(workspaceBadgeQuery.data?.counts.blog_interactions ?? 0) };
      }
      if (action.label === "Eventet") {
        return { ...action, badge: workspaceBadgeQuery.isLoading ? "..." : compactCarouselValue(workspaceBadgeQuery.data?.counts.event_interactions ?? 0) };
      }
      return action;
    })
    : null;
  const businessShortcutVariant = pathname === "/app/service-hub" && new URLSearchParams(search).get("business_picker") === "tabs"
    ? "tabs"
    : "cards";
  const canUseMarketplaceBadges =
    activeFunctionalAccountType !== null &&
    (marketplaceBadgeAccountTypes.includes(activeFunctionalAccountType as (typeof marketplaceBadgeAccountTypes)[number]) ||
      capabilities.includes("marketplace.cart") ||
      capabilities.includes("marketplace.favorites"));
  const navBadgesQuery = useNavBadges(canUseMarketplaceBadges);
  const favoriteBadgeCount = Number(navBadgesQuery.data?.counts.favorites ?? 0);
  const cartBadgeCount = Number(navBadgesQuery.data?.counts.cart ?? 0);
  const canUseMessaging = capabilities.some(
    (capability) => capability === "messages.general" || capability === "messages.clinical",
  );
  const { data: unreadMessages = 0 } = useMsnUnreadCount(canUseMessaging);
  const isShowcaseRoute = pathname === "/";
  const notificationsQuery = useNotifications();
  const unreadNotifications = (notificationsQuery.data ?? []).filter((notification) => !notification.is_read).length;
  const showcaseInboxCount = unreadNotifications + unreadMessages;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isShowcaseMobileDockOpen, setIsShowcaseMobileDockOpen] = useState(false);
  const [isSmartSearchOpen, setIsSmartSearchOpen] = useState(false);
  // isNotificationsOpen removed â€” replaced by NotificationBell component
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountMenuPanelRef = useRef<HTMLDivElement>(null);
  const topChromeRef = useRef<HTMLDivElement>(null);
  const sidebarCollapsePreferenceBeforePublicRoute = useRef<boolean | null>(null);
  const activePublicRouteRef = useRef<string | null>(null);
  const [topChromeHeight, setTopChromeHeight] = useState(64);
  // Admin/SuperAdmin kalojnÃ« onboarding-un â€” nuk kanÃ« nevojÃ« pÃ«r profil aktiv
  const displayName = user?.fullName ?? user?.email ?? t("common.session");
  const accountInitial = (displayName.trim().charAt(0) || "U").toUpperCase();
  const accountAvatarUrl = getMediaUrl(user?.avatarUrl ?? null);
  const isSupportMode = Boolean(user?.isImpersonating);
  const openSmartSearch = (): void => {
    setIsSmartSearchOpen(true);
  };

  useEffect(() => {
    const onDocumentKeyDown = (event: KeyboardEvent): void => {
      const isSearchShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      if (!isSearchShortcut) {
        return;
      }
      event.preventDefault();
      setIsSmartSearchOpen(true);
    };

    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, []);

  useEffect(() => {
    if (user === null) {
      sidebarCollapsePreferenceBeforePublicRoute.current = null;
      activePublicRouteRef.current = null;
      return;
    }

    if (isPublicContentRoute(pathname)) {
      if (activePublicRouteRef.current !== pathname) {
        sidebarCollapsePreferenceBeforePublicRoute.current = useAuthStore.getState().isSidebarCollapsed;
        activePublicRouteRef.current = pathname;
        setSidebarCollapsed(true);
      }
      return;
    }

    activePublicRouteRef.current = null;
    if (sidebarCollapsePreferenceBeforePublicRoute.current !== null) {
      setSidebarCollapsed(sidebarCollapsePreferenceBeforePublicRoute.current);
      sidebarCollapsePreferenceBeforePublicRoute.current = null;
    }
  }, [pathname, setSidebarCollapsed, user]);

  useEffect(() => {
    if (!isAccountMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      const isInsideDesktopMenu = accountMenuRef.current?.contains(target) ?? false;
      const isInsideMobileMenu = accountMenuPanelRef.current?.contains(target) ?? false;
      if (!isInsideDesktopMenu && !isInsideMobileMenu) {
        setIsAccountMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  useEffect(() => {
    const updateTopChromeHeight = (): void => {
      const height = topChromeRef.current?.getBoundingClientRect().height ?? 64;
      setTopChromeHeight(Math.ceil(height));
    };

    updateTopChromeHeight();

    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateTopChromeHeight);
    if (topChromeRef.current && observer) {
      observer.observe(topChromeRef.current);
    }
    window.addEventListener("resize", updateTopChromeHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateTopChromeHeight);
    };
  }, []);

  const supportTopOffset = isSupportMode ? 40 : 0;
  const topChromeTopClass = isSupportMode ? "top-10" : "top-0";
  const sidebarTopOffset = supportTopOffset + topChromeHeight;

  return (
    <div className={cn("min-h-screen bg-background text-[hsl(var(--text))]", isSupportMode && "pt-10")}>
      {isSupportMode ? (
        <div className="fixed inset-x-0 top-0 z-[60] flex min-h-10 items-center justify-center gap-2 bg-red-700 px-4 py-2 text-center text-sm font-semibold text-white shadow-lg">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Keni hyrÃ« si {displayName}. Qasja nÃ« tÃ« dhÃ«nat klinike Ã«shtÃ« e bllokuar automatikisht.</span>
        </div>
      ) : null}
      <div ref={topChromeRef} className={cn("fixed inset-x-0 z-40", topChromeTopClass)}>
        <PromoRibbonStrip />
        <header className="border-b border-border bg-[hsl(var(--surface))]/95 backdrop-blur-xl">
        <div className="flex h-16 min-w-0 items-center gap-2 px-3 sm:gap-3 sm:px-4">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => setIsMobileNavOpen(true)}
            aria-label="Hap navigimin"
            className={cn("md:hidden", isShowcaseRoute && "hidden")}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={toggleSidebar}
            aria-label="Hap/mbyll navigimin"
            className="hidden md:inline-flex"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <Link
            to="/"
            onClick={() => {
              if (user !== null) {
                setSidebarCollapsed(true);
              }
            }}
            className={cn("flex min-w-0 shrink items-center gap-2 font-bold text-primary transition hover:opacity-80", isShowcaseRoute && "text-fuchsia-600 md:text-primary")}
          >
            <ShieldCheck className={cn("h-7 w-7 shrink-0", isShowcaseRoute && "hidden sm:block")} aria-hidden="true" />
            <span className={cn("hidden truncate sm:inline", isShowcaseRoute && "inline font-serif text-xl italic sm:text-base sm:not-italic")}>{isShowcaseRoute ? "eshendetesia" : t("common.appName")}</span>
          </Link>
          <div className="hidden min-w-0 flex-1 justify-center md:flex">
            <div className="flex w-full max-w-xl items-center">
            <div className="relative w-full">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                className="h-10 pl-10"
                placeholder={t("common.searchPlaceholder")}
                value={globalSearch}
                onChange={(event) => setGlobalSearch(event.target.value)}
                onFocus={openSmartSearch}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    openSmartSearch();
                  }
                }}
              />
            </div>
            <Button type="button" variant="ghost" className="ml-2 shrink-0" onClick={openSmartSearch}>
              {t("common.search")}
            </Button>
            </div>
          </div>
          {isShowcaseRoute ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label="Hap shiritin e shkurtesave"
              aria-expanded={isShowcaseMobileDockOpen}
              onClick={() => setIsShowcaseMobileDockOpen((current) => !current)}
              className="relative ml-auto rounded-full border border-border bg-card shadow-sm md:hidden"
            >
              <MessageSquare className="h-5 w-5" aria-hidden="true" />
              {showcaseInboxCount > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground ring-1 ring-background">
                  {formatNavBadgeCount(showcaseInboxCount)}
                </span>
              ) : null}
            </Button>
          ) : null}
          <div className={cn("ml-auto flex min-w-0 shrink-0 items-center gap-1 sm:gap-2 md:ml-0", isShowcaseRoute && "hidden md:flex")}>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={t("common.search")}
              onClick={openSmartSearch}
              className="md:hidden"
            >
              <Search className="h-5 w-5" aria-hidden="true" />
            </Button>
            <div className="relative">
              <Button type="button" size="icon" variant="ghost" aria-label={t("common.favorites")} onClick={() => navigate("/app/favorites")}>
                <Heart className="h-5 w-5" aria-hidden="true" />
              </Button>
              {favoriteBadgeCount > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground ring-1 ring-background">
                  {formatNavBadgeCount(favoriteBadgeCount)}
                </span>
              ) : null}
            </div>
            <NotificationPopover />
            <div className="relative">
              <Button type="button" size="icon" variant="ghost" aria-label={t("common.messages")} onClick={() => navigate("/app/messages")}>
                <MessageSquare className="h-5 w-5" aria-hidden="true" />
              </Button>
              {unreadMessages > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                  {unreadMessages > 9 ? "9+" : unreadMessages}
                </span>
              ) : null}
            </div>
            <div className="relative">
              <Button type="button" size="icon" variant="ghost" aria-label={t("common.cart")} onClick={() => navigate("/app/cart")}>
                <ShoppingCart className="h-5 w-5" aria-hidden="true" />
              </Button>
              {cartBadgeCount > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground ring-1 ring-background">
                  {formatNavBadgeCount(cartBadgeCount)}
                </span>
              ) : null}
            </div>
            <div ref={accountMenuRef} className="relative">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Hap menunÃ« e llogarisÃ«"
                aria-expanded={isAccountMenuOpen}
                onClick={() => setIsAccountMenuOpen((current) => !current)}
                className="rounded-full border border-border bg-card shadow-sm"
              >
                <AccountAvatar avatarUrl={accountAvatarUrl} displayName={displayName} fallback={accountInitial} sizeClassName="h-8 w-8 text-sm" />
              </Button>
              {isAccountMenuOpen ? (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-[64] hidden cursor-default md:block"
                    aria-label="Mbyll menunÃ« e llogarisÃ«"
                    onClick={() => setIsAccountMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-12 z-[72] hidden w-80 rounded-[24px] border border-border bg-card p-4 text-card-foreground shadow-2xl md:block">
                    <AccountMenuContent
                      displayName={displayName}
                      email={user?.email ?? null}
                      avatarUrl={accountAvatarUrl}
                      theme={theme}
                      setTheme={setTheme}
                      onClose={() => setIsAccountMenuOpen(false)}
                      onNavigate={(path) => { navigate(path); setIsAccountMenuOpen(false); }}
                      onLogout={() => { setIsAccountMenuOpen(false); logoutMutation.mutate(); }}
                      logoutDisabled={logoutMutation.isPending}
                      t={t}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
        </header>
      </div>

      {isAccountMenuOpen ? (
        <div className="fixed inset-0 z-[72] md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-background/70 backdrop-blur-sm"
            aria-label="Mbyll menunÃ« e llogarisÃ«"
            onClick={() => setIsAccountMenuOpen(false)}
          />
          <div ref={accountMenuPanelRef} className="fixed inset-x-3 bottom-3 w-auto overflow-y-auto rounded-[24px] border border-border bg-card p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] text-card-foreground shadow-2xl [max-height:min(70vh,520px)]">
            <AccountMenuContent
              displayName={displayName}
              email={user?.email ?? null}
              avatarUrl={accountAvatarUrl}
              theme={theme}
              setTheme={setTheme}
              onClose={() => setIsAccountMenuOpen(false)}
              onNavigate={(path) => { navigate(path); setIsAccountMenuOpen(false); }}
              onLogout={() => { setIsAccountMenuOpen(false); logoutMutation.mutate(); }}
              logoutDisabled={logoutMutation.isPending}
              t={t}
            />
          </div>
        </div>
      ) : null}

      <SmartSearchCommandPalette
        open={isSmartSearchOpen}
        query={globalSearch}
        onQueryChange={setGlobalSearch}
        onClose={() => setIsSmartSearchOpen(false)}
      />

      {isMobileNavOpen ? (
        <div className="fixed inset-0 z-[68] md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-background/70 backdrop-blur-sm"
            aria-label="Mbyll navigimin"
            onClick={() => setIsMobileNavOpen(false)}
          />
          <aside className="relative flex h-full w-80 max-w-[86vw] flex-col overflow-y-auto border-r border-border bg-card p-4 text-card-foreground shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2 font-bold text-primary">
                <ShieldCheck className="h-7 w-7" aria-hidden="true" />
                <span>{t("common.appName")}</span>
              </Link>
              <Button type="button" size="icon" variant="ghost" aria-label="Mbyll navigimin" onClick={() => setIsMobileNavOpen(false)}>
                <X className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
            <SidebarNav onNavigate={() => setIsMobileNavOpen(false)} />
          </aside>
        </div>
      ) : null}

      {isShowcaseRoute ? (
        <ShowcaseMobileDock
          open={isShowcaseMobileDockOpen}
          displayName={displayName}
          avatarUrl={accountAvatarUrl}
          favoriteBadgeCount={favoriteBadgeCount}
          cartBadgeCount={cartBadgeCount}
          unreadMessages={unreadMessages}
          onClose={() => setIsShowcaseMobileDockOpen(false)}
          onOpenNavigation={() => {
            setIsShowcaseMobileDockOpen(false);
            setIsMobileNavOpen(true);
          }}
          onNavigate={(href) => {
            setIsShowcaseMobileDockOpen(false);
            navigate(href);
          }}
        />
      ) : null}

      <div className="flex" style={{ paddingTop: topChromeHeight }}>
        <aside
          className={cn(
            "fixed left-0 z-30 hidden shrink-0 overflow-y-auto border-r border-border bg-[hsl(var(--surface))]/90 p-3 pb-10 backdrop-blur-xl transition-all md:block",
            isSidebarCollapsed ? "w-16" : "w-72",
          )}
          style={{ top: sidebarTopOffset, height: `calc(100vh - ${sidebarTopOffset}px)` }}
        >
          <SidebarNav collapsed={isSidebarCollapsed} />
        </aside>

        <div className={cn("flex min-w-0 flex-1 flex-col overflow-x-hidden transition-[margin] duration-200", isSidebarCollapsed ? "md:ml-16" : "md:ml-72")}>
        <HybridModeBanner />
        <main className="min-w-0 flex-1 overflow-x-hidden p-4 lg:p-6">
          {pathname !== "/app/dashboard" ? (
            <GlobalBackButton
              fallbackHref="/app/dashboard"
              className="top-auto md:top-auto"
              style={{
                left: isSidebarCollapsed ? 76 : 300,
                top: topChromeHeight + 14,
              }}
            />
          ) : null}
            {showWorkspaceShortcuts && previewRole ? (
              <div className="mx-auto mb-4 w-full max-w-7xl">
                <WorkspaceShortcutCarousel actions={doctorShortcutActions ?? getPreviewQuickActions(previewRole)} />
              </div>
          ) : null}
          {showBusinessWorkspaceShortcuts && businessWorkspaceRole ? (
            <div className="mx-auto mb-4 w-full max-w-7xl">
              <WorkspaceShortcutCarousel actions={getBusinessWorkspaceQuickActions(businessWorkspaceRole)} variant={businessShortcutVariant} />
            </div>
          ) : null}
          {children ?? <Outlet />}
          <Footer />
          {/* PiP video â€” persist-on jashtÃ« /app/messages */}
          <FloatingVideoCall />
        </main>
        </div>
      </div>
      <FloatingAccountDock />
      <FloatingShareControl />
    </div>
  );
};

function ShowcaseMobileDock({
  open,
  displayName,
  avatarUrl,
  favoriteBadgeCount,
  cartBadgeCount,
  unreadMessages,
  onClose,
  onOpenNavigation,
  onNavigate,
}: {
  open: boolean;
  displayName: string;
  avatarUrl: string | null;
  favoriteBadgeCount: number;
  cartBadgeCount: number;
  unreadMessages: number;
  onClose: () => void;
  onOpenNavigation: () => void;
  onNavigate: (href: string) => void;
}): JSX.Element {
  if (!open) return <></>;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[67] border-t border-white/10 bg-slate-950/95 px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 text-white shadow-[0_-12px_32px_rgba(15,23,42,0.28)] backdrop-blur-xl md:hidden">
      <div className="mx-auto flex max-w-md items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Shkurtesat e Showcase">
        <DockButton icon={<Search className="h-5 w-5" />} label="Kerko" onClick={() => onNavigate("/search?advanced=1")} />
        <DockButton icon={<Menu className="h-5 w-5" />} label="Menuja" onClick={onOpenNavigation} />
        <DockButton icon={<ShieldCheck className="h-5 w-5" />} label="Showcase" onClick={() => onNavigate("/")} />
        <DockButton icon={<Heart className="h-5 w-5" />} label="Preferencat" badge={favoriteBadgeCount} onClick={() => onNavigate("/app/favorites")} />
        <div className="[&_button]:border-white/15 [&_button]:bg-white/5 [&_button]:text-white">
          <NotificationPopover />
        </div>
        <DockButton icon={<MessageSquare className="h-5 w-5" />} label="Mesazhet" badge={unreadMessages} onClick={() => onNavigate("/app/messages")} />
        <DockButton icon={<ShoppingCart className="h-5 w-5" />} label="Shporta" badge={cartBadgeCount} onClick={() => onNavigate("/app/cart")} />
        <button type="button" title="Profili im" aria-label="Profili im" onClick={() => onNavigate("/app/dashboard")} className="grid h-10 w-10 place-items-center rounded-full border border-sky-300/60 bg-sky-500/20 text-white transition hover:bg-sky-500/35">
          <AccountAvatar avatarUrl={avatarUrl} displayName={displayName} fallback={(displayName.trim().charAt(0) || "U").toUpperCase()} sizeClassName="h-8 w-8 text-xs" />
        </button>
        <DockButton icon={<X className="h-4 w-4" />} label="Mbyll shiritin" onClick={onClose} />
      </div>
    </div>
  );
}

function DockButton({ icon, label, badge = 0, onClick }: { icon: JSX.Element; label: string; badge?: number; onClick: () => void }): JSX.Element {
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} className="relative grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-100 transition hover:bg-white/10">
      {icon}
      {badge > 0 ? <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-sky-500 px-0.5 text-[9px] font-bold text-white">{formatNavBadgeCount(badge)}</span> : null}
    </button>
  );
}

function AccountMenuContent({
  displayName,
  email,
  avatarUrl,
  theme,
  setTheme,
  onClose,
  onNavigate,
  onLogout,
  logoutDisabled,
  t,
}: {
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  onClose: () => void;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  logoutDisabled: boolean;
  t: (key: string) => string;
}): JSX.Element {
  return (
    <div className="min-w-0 text-sm">
      <div className="flex min-w-0 items-center gap-2.5 border-b border-border pb-3">
        <button
          type="button"
          onClick={() => onNavigate("/app/account")}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-2xl p-1.5 text-left transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="Hap llogarine"
        >
        <AccountAvatar
          avatarUrl={avatarUrl}
          displayName={displayName}
          fallback={(displayName.trim().charAt(0) || "U").toUpperCase()}
          sizeClassName="h-10 w-10 text-base"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-card-foreground">{displayName}</p>
          <p className="truncate text-xs text-muted-foreground">{email ?? "â€”"}</p>
        </div>
        </button>
        <Button type="button" size="icon" variant="ghost" aria-label="Mbyll menunÃ« e llogarisÃ«" onClick={onClose} className="h-8 w-8 shrink-0">
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {/* <div className="mt-3 grid gap-1.5">
        <button
          type="button"
          onClick={() => onNavigate("/app/account")}
          className="flex w-full items-center gap-2.5 rounded-2xl px-3 py-2 text-left text-card-foreground transition hover:bg-secondary"
        >
          <UserCircle className="h-4 w-4 text-primary" aria-hidden="true" />
          <span>Llogaria / Profili</span>
        </button>
        <button
          type="button"
          onClick={() => onNavigate("/app/account/security")}
          className="flex w-full items-center gap-2.5 rounded-2xl px-3 py-2 text-left text-card-foreground transition hover:bg-secondary"
        >
          <Settings className="h-4 w-4 text-primary" aria-hidden="true" />
          <span>Siguria dhe cilÃ«simet</span>
        </button>
      </div> */}

      <div className="mt-3 border-t border-border pt-3">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t("common.theme")}</p>
        <div className="grid grid-cols-3 gap-1.5">
          {themeOptions.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={theme === option.value ? "primary" : "outline"}
              className="h-8 rounded-[20px] px-2 text-[11px]"
              onClick={() => setTheme(option.value)}
            >
              {t(option.labelKey)}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t("common.language")}</p>
        <div className="grid grid-cols-4 gap-1.5">
          {languageOptions.map((language) => (
            <Button
              key={language}
              type="button"
              size="sm"
              variant={i18n.language === language ? "primary" : "outline"}
              className="h-8 rounded-[20px] px-2 text-[11px]"
              onClick={() => void i18n.changeLanguage(language)}
            >
              {t(`languages.${language}`)}
            </Button>
          ))}
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="mt-3 h-9 w-full justify-center rounded-2xl border-destructive/30 text-destructive hover:bg-destructive/10"
        disabled={logoutDisabled}
        onClick={onLogout}
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
        {t("common.logout")}
      </Button>
    </div>
  );
}

function AccountAvatar({
  avatarUrl,
  displayName,
  fallback,
  sizeClassName,
}: {
  avatarUrl: string | null;
  displayName: string;
  fallback: string;
  sizeClassName: string;
}): JSX.Element {
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [avatarUrl]);

  return (
    <span className={cn("grid shrink-0 place-items-center overflow-hidden rounded-full bg-primary font-bold text-primary-foreground", sizeClassName)}>
      {avatarUrl && !hasImageError ? (
        <img
          src={avatarUrl}
          alt={displayName}
          className="h-full w-full object-cover"
          onError={() => setHasImageError(true)}
        />
      ) : (
        fallback
      )}
    </span>
  );
}
